import { Grid } from '../../core/Grid.js';
import {
    Position,
    Direction,
    positionKey,
    parsePositionKey,
    isInside
} from '../../core/GridGeometry.js';
import { MirrorsGame, MirrorType, LaserPath } from './MirrorsGame.js';
import { ScoreBreakdown, calculateScore } from '../../core/Scoring.js';
import { VortexGateManager } from '../VortexGateManager.js';
import { IVortexGame } from '../IVortexGame.js';

export class MirrorsVortex extends MirrorsGame implements IVortexGame {
    private readonly gm = new VortexGateManager();

    constructor(grid: Grid) {
        super(grid);
    }

    // ── Vortex Data ───────────────────────────────────────────────

    setVortexData(gatePairs: Array<[Position, Position]>, allGates: Set<string>): void {
        this.gm.setVortexData(gatePairs, allGates, () => {
            this.currentPath = null;
            this.reversePath = null;
        });
    }

    // ── Gate Queries ──────────────────────────────────────────────

    isGateCell(pos: Position): boolean {
        return this.gm.isGateCell(pos);
    }

    isActiveGate(pos: Position): boolean {
        return this.gm.isActiveGate(pos);
    }

    canActivateGate(pos: Position): boolean {
        return this.gm.canActivateGate(pos, (p, paired) => {
            const key = positionKey(p);
            const pairedKey = positionKey(paired);
            if (this.mirrors.has(key)) return false;
            if (this.mirrors.has(pairedKey)) return false;
            if (this.grid.hasDigitAt(p)) return false;
            if (this.grid.hasDigitAt(paired)) return false;
            return true;
        });
    }

    toggleVortexGate(pos: Position): boolean {
        return this.gm.toggleVortexGate(pos, this.canActivateGate(pos), () => {
            this.currentPath = null;
            this.reversePath = null;
        });
    }

    getActiveGatePairs(): Map<string, string> {
        return this.gm.getActiveGatePairs();
    }

    getVortexGatePairs(): Array<[Position, Position]> {
        return this.gm.getVortexGatePairs();
    }

    getAllGatePositions(): Set<string> {
        return this.gm.getAllGatePositions();
    }

    // ── Scoring ────────────────────────────────────────────────────

    override getScore(): ScoreBreakdown | null {
        if (!this.checkWin()) return null;

        const path = this.traceLaser();
        const mirrorCount = this.mirrors.size;

        const visitedCells = new Set<string>();
        for (const segment of path.segments) {
            const key = positionKey(segment.from);
            if (!this.grid.hasDigitAt(segment.from)) {
                visitedCells.add(key);
            }
        }

        return calculateScore(mirrorCount, this.gm.deduplicateCellCount(visitedCells));
    }

    // ── Placement Blocking ────────────────────────────────────────

    override setMirror(pos: Position, mirror: MirrorType | null): void {
        if (mirror !== null && this.gm.isActiveGate(pos)) return;
        super.setMirror(pos, mirror);
    }

    override cycleMirror(pos: Position): void {
        if (this.gm.isActiveGate(pos)) return;
        super.cycleMirror(pos);
    }

    // ── State Management ──────────────────────────────────────────

    override reset(): void {
        super.reset();
        this.gm.clearActiveGates();
    }

    override isStarted(): boolean {
        return super.isStarted() || this.gm.hasActiveGates();
    }

    // ── Beam Teleportation ────────────────────────────────────────

    override traceLaser(): LaserPath {
        if (this.currentPath) return this.currentPath;

        if (!this.gm.hasActiveGates()) {
            return super.traceLaser();
        }

        this.currentPath = this.traceBeam(
            { row: 0, col: 0 },
            'E' as Direction,
            this.grid.size - 1,
            this.grid.size - 1,
            'E' as Direction
        );
        return this.currentPath;
    }

    override traceReverseLaser(): LaserPath {
        if (this.reversePath) return this.reversePath;

        if (!this.gm.hasActiveGates()) {
            return super.traceReverseLaser();
        }

        this.reversePath = this.traceBeam(
            { row: this.grid.size - 1, col: this.grid.size - 1 },
            'W' as Direction,
            0,
            0,
            'W' as Direction
        );
        return this.reversePath;
    }

    private traceBeam(
        startPos: Position,
        startDir: Direction,
        exitRow: number,
        exitCol: number,
        exitDir: Direction
    ): LaserPath {
        const segments: import('./MirrorsGame.js').LaserSegment[] = [];
        const digitsHit: number[] = [];
        const digitsCrossedTwice: number[] = [];
        const visitedStates = new Set<string>();

        let pos = startPos;
        let dir = startDir;
        let justTeleported = false;

        const maxIterations = this.grid.size * this.grid.size * 4;
        let iterations = 0;

        while (iterations < maxIterations) {
            iterations++;
            const key = positionKey(pos);

            const stateKey = `${key}:${dir}:${justTeleported}`;
            if (visitedStates.has(stateKey)) break;
            visitedStates.add(stateKey);

            // Check for digit
            const digit = this.grid.getDigitAt(pos);
            if (digit !== null) {
                if (digitsHit.includes(digit)) {
                    if (!digitsCrossedTwice.includes(digit)) {
                        digitsCrossedTwice.push(digit);
                    }
                } else {
                    digitsHit.push(digit);
                }
            }

            // Mirror reflection
            const mirror = this.getMirrorAt(pos);
            let newDir: Direction = dir;
            if (mirror) {
                newDir = this.reflectDirection(dir, mirror);
            }

            // Vortex gate teleportation (skip if we just arrived here via teleport)
            if (!justTeleported) {
                const pairedKey = this.gm.raw.get(key);
                if (pairedKey) {
                    const pairedPos = parsePositionKey(pairedKey);
                    segments.push({
                        from: { ...pos },
                        to: { ...pairedPos },
                        direction: newDir,
                        teleport: true
                    });
                    pos = pairedPos;
                    dir = newDir;
                    justTeleported = true;
                    continue;
                }
            }
            justTeleported = false;

            // Normal move
            const nextPosOrOutside = this.grid.move(pos, newDir);

            segments.push({
                from: { ...pos },
                to: { row: nextPosOrOutside.row, col: nextPosOrOutside.col },
                direction: newDir
            });

            if (!isInside(nextPosOrOutside)) {
                const isCorrectExit =
                    pos.row === exitRow &&
                    pos.col === exitCol &&
                    newDir === exitDir;

                return {
                    segments,
                    digitsHit,
                    digitsCrossedTwice,
                    exited: true,
                    exitPosition: isCorrectExit ? pos : null
                };
            }

            pos = nextPosOrOutside;
            dir = newDir;
        }

        return {
            segments,
            digitsHit,
            digitsCrossedTwice,
            exited: false,
            exitPosition: null
        };
    }

    // ── Serialization ─────────────────────────────────────────────

    override serialize(): string {
        return this.gm.serializeWithBase(super.serialize(), this.gs);
    }

    override deserialize(serialized: string): void {
        this.gm.clearActiveGates();
        if (!serialized) {
            super.deserialize('');
            return;
        }

        const { basePart, gatePart } = VortexGateManager.splitSerialized(serialized);
        super.deserialize(basePart);
        this.gm.deserializeGates(gatePart, this.gs);
        this.currentPath = null;
    }
}
